// Shared workload maths — used by the Calendar chip bars AND the Dashboard
// overload alert. Keeping both callers on the same helper means "how many
// hours" is answered identically everywhere.

export const DOW_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const DEFAULT_WORKDAY_HOURS = { mon: 8, tue: 8, wed: 8, thu: 8, fri: 8, sat: 0, sun: 0 };
export const WEEKLY_CAPACITY_HOURS = 40;

export function dayOfWeekKey(d) {
  const js = d.getDay(); // 0=Sun ... 6=Sat
  const idx = js === 0 ? 6 : js - 1; // shift so Mon=0
  return DOW_KEYS[idx];
}

// Monday 00:00 of the week that contains `d`.
export function startOfMondayWeek(d) {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  const dow = r.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  r.setDate(r.getDate() + diff);
  return r;
}

// Sunday 23:59:59.999 of the week that contains `d`.
export function endOfMondayWeek(d) {
  const start = startOfMondayWeek(d);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

function parseYMD(s) {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function weeklyCapacityFromHours(workdayHours) {
  return DOW_KEYS.reduce((s, k) => s + (Number(workdayHours?.[k]) > 0 ? Number(workdayHours[k]) : 0), 0);
}

// Same logic as the Calendar chip — precise timeline slots when present,
// per-day fallback via assigned_to otherwise. Weekend days count 0 by default
// unless the admin's workday_hours setting opts them in.
export function computeInstallerHours(installerId, jobs, rangeStart, rangeEnd, workdayHours = DEFAULT_WORKDAY_HOURS) {
  if (!installerId) return 0;
  let hours = 0;
  const startMs = rangeStart.getTime();
  const endMs = rangeEnd.getTime();
  for (const j of jobs) {
    const jobHasTimeline = Array.isArray(j.timeline) && j.timeline.length > 0;
    if (jobHasTimeline) {
      for (const s of j.timeline) {
        if (s.installer_id !== installerId) continue;
        const sa = s.start_at ? new Date(s.start_at).getTime() : NaN;
        const ea = s.end_at ? new Date(s.end_at).getTime() : NaN;
        if (!Number.isFinite(sa) || !Number.isFinite(ea) || ea <= sa) continue;
        const overlap = Math.max(0, Math.min(ea, endMs) - Math.max(sa, startMs));
        hours += overlap / 3_600_000;
      }
      continue;
    }
    if (j.assigned_to !== installerId || !j.scheduled_date) continue;
    const s = parseYMD(j.scheduled_date);
    const e = parseYMD(j.scheduled_end_date) || s;
    if (!s) continue;
    const jobStart = new Date(s); jobStart.setHours(0, 0, 0, 0);
    const jobEnd = new Date(e); jobEnd.setHours(0, 0, 0, 0);
    const winStart = new Date(rangeStart); winStart.setHours(0, 0, 0, 0);
    const winEnd = new Date(rangeEnd); winEnd.setHours(0, 0, 0, 0);
    if (jobEnd.getTime() < winStart.getTime() || jobStart.getTime() > winEnd.getTime()) continue;
    const iterStart = new Date(Math.max(jobStart.getTime(), winStart.getTime()));
    const iterEnd = new Date(Math.min(jobEnd.getTime(), winEnd.getTime()));
    for (let d = new Date(iterStart); d.getTime() <= iterEnd.getTime(); d.setDate(d.getDate() + 1)) {
      const key = dayOfWeekKey(d);
      const h = Number(workdayHours[key]);
      if (Number.isFinite(h) && h > 0) hours += h;
    }
  }
  return Math.round(hours * 10) / 10;
}
